import React, { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { ArrowLeft, Calendar, User, Tag, BookOpen, Loader2 } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

export default function BlogPost() {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    axios.get(`${API}/blog/${slug}`)
      .then((r) => setPost(r.data))
      .catch(() => setNotFound(true))
      .finally(() => setLoading(false));
    window.scrollTo(0, 0);
  }, [slug]);

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center pt-24">
      <Loader2 size={28} color="#00F3FF" className="animate-spin" />
    </div>
  );

  if (notFound) return (
    <div className="min-h-screen flex flex-col items-center justify-center pt-24 text-center px-6">
      <h1 className="font-orbitron font-900 text-4xl text-white mb-4">Post Not Found</h1>
      <Link to="/blog" className="no-underline">
        <button className="btn-neon flex items-center gap-2 px-6 py-3">
          <ArrowLeft size={14} /> BACK TO BLOG
        </button>
      </Link>
    </div>
  );

  const date = new Date(post.created_at).toLocaleDateString("en-IN", { day: "numeric", month: "long", year: "numeric" });

  return (
    <div data-testid="blog-post-page" className="page-enter pt-24">
      {/* Back */}
      <div className="max-w-3xl mx-auto px-6 pt-8">
        <Link to="/blog" className="no-underline">
          <button className="flex items-center gap-2 text-sm text-gray-500 hover:text-white font-outfit transition-colors mb-8">
            <ArrowLeft size={14} /> Back to Blog
          </button>
        </Link>
      </div>

      {/* Hero cover */}
      {post.cover_image && (
        <div className="max-w-5xl mx-auto px-6 mb-10">
          <div className="rounded-2xl overflow-hidden h-64 md:h-96">
            <img src={post.cover_image} alt={post.title}
              className="w-full h-full object-cover"
              onError={(e) => { e.target.parentElement.style.display = "none"; }} />
          </div>
        </div>
      )}

      {/* Content */}
      <article className="max-w-3xl mx-auto px-6 pb-20">
        {/* Tags */}
        {post.tags?.length > 0 && (
          <div className="flex flex-wrap gap-2 mb-5">
            {post.tags.map((tag) => (
              <span key={tag} className="text-xs font-outfit px-2.5 py-1 rounded-full flex items-center gap-1"
                style={{ background: "rgba(0,243,255,0.08)", color: "#00F3FF", border: "1px solid rgba(0,243,255,0.2)" }}>
                <Tag size={9} /> {tag}
              </span>
            ))}
          </div>
        )}

        <h1 className="font-orbitron font-900 text-3xl lg:text-4xl text-white leading-tight mb-5">{post.title}</h1>

        <div className="flex items-center gap-5 mb-8 pb-6 border-b" style={{ borderColor: "rgba(255,255,255,0.06)" }}>
          <div className="flex items-center gap-1.5 text-sm text-gray-500 font-outfit">
            <User size={12} color="#00F3FF" /> {post.author}
          </div>
          <div className="flex items-center gap-1.5 text-sm text-gray-500 font-outfit">
            <Calendar size={12} color="#BC13FE" /> {date}
          </div>
        </div>

        {/* Post content */}
        <div
          className="font-outfit text-gray-300 leading-relaxed blog-content"
          style={{ fontWeight: 300 }}
          dangerouslySetInnerHTML={{ __html: post.content }}
        />

        {/* Footer CTA */}
        <div className="mt-12 rounded-2xl p-8 text-center"
          style={{ background: "linear-gradient(135deg, rgba(0,243,255,0.06), rgba(188,19,254,0.06))", border: "1px solid rgba(0,243,255,0.15)" }}>
          <h3 className="font-orbitron font-700 text-xl text-white mb-3">Ready to Get Started?</h3>
          <p className="font-outfit text-sm text-gray-400 mb-5" style={{ fontWeight: 300 }}>
            Join 50,000+ businesses on IndiaCloud.ai
          </p>
          <div className="flex items-center justify-center gap-3 flex-wrap">
            <Link to="/plan-recommender" className="no-underline">
              <button className="btn-primary text-sm px-6 py-2.5">AI PICK MY PLAN</button>
            </Link>
            <Link to="/blog" className="no-underline">
              <button className="btn-neon text-sm px-6 py-2.5">MORE ARTICLES</button>
            </Link>
          </div>
        </div>
      </article>

      <style>{`
        .blog-content h2 { font-family: 'Rajdhani', sans-serif; font-weight: 700; font-size: 1.4rem; color: #00F3FF; margin: 2rem 0 1rem; }
        .blog-content h3 { font-family: 'Rajdhani', sans-serif; font-weight: 600; font-size: 1.2rem; color: #fff; margin: 1.5rem 0 0.75rem; }
        .blog-content p { margin-bottom: 1rem; line-height: 1.8; }
        .blog-content ul, .blog-content ol { padding-left: 1.5rem; margin-bottom: 1rem; }
        .blog-content li { margin-bottom: 0.5rem; }
        .blog-content strong { color: #fff; font-weight: 600; }
        .blog-content a { color: #00F3FF; text-decoration: underline; }
      `}</style>
    </div>
  );
}
